// Gabungkan dokumen on a project page, the same for both sides (API POST
// /activity-docs/:id/merge): one Word file of every activity's newest saved
// version, in the order of work, for the stage on view or (without `stage`)
// the whole project. Activities without a saved version are marked
// [BELUM ADA DOKUMEN] in the file; the line under the button says how many,
// and whether a copy was kept in the Drive. A Non-Penyedia file holds only the
// activities of the mode in use (B12), which the line says before a merge.
import { useState } from "react";
import * as API from "../api";
import { useMaybeUser } from "../auth";
import { NON_PENYEDIA_ROLES, ROLE_LABEL } from "../lib/labels";
import { saveFile } from "../pbj/docx";
import type { FeatureState } from "../shell/features";
import { Glyph } from "../ui/icons";

/** The route's own gates: projects, the template library and document generation. */
export function canMergeDocs(features: FeatureState): boolean {
  return features.list !== null && features.has("projects") && features.has("templates") && features.has("document_generation");
}

type MergeState = { kind: "idle" } | { kind: "busy" } | { kind: "done"; result: API.MergedDocs } | { kind: "error"; message: string };

/** The mode a Non-Penyedia merge is for ("PPK"), or null (Penyedia, admin). */
export function mergeModeLabel(role: API.User["role"] | undefined): string | null {
  return role && (NON_PENYEDIA_ROLES as readonly string[]).includes(role) ? ROLE_LABEL[role] : null;
}

/** What the line under the button says after a merge. */
export function mergeNote(r: Pick<API.MergedDocs, "activities" | "missing" | "stored">, mode: string | null = null): string {
  const parts = [`${r.activities} dokumen aktivitas${mode ? ` mode ${mode}` : ""} digabung`];
  if (r.missing > 0) parts.push(`${r.missing} belum ada dokumen tersimpan (ditandai di file)`);
  return `${parts.join(", ")}.${r.stored ? " Salinannya tersimpan di Drive." : ""}`;
}

/**
 * `modeHint`: "line" says the mode under the button before a merge; "title"
 * only in its tooltip (a crowded header row, where the line would wrap it).
 */
export function MergeDocsButton({
  projectId,
  stage,
  label,
  size = "md",
  modeHint = "line",
}: {
  projectId: string;
  stage?: string;
  label: string;
  size?: "sm" | "md";
  modeHint?: "line" | "title";
}) {
  const [state, setState] = useState<MergeState>({ kind: "idle" });
  const mode = mergeModeLabel(useMaybeUser()?.role);
  const run = async () => {
    setState({ kind: "busy" });
    try {
      const result = await API.mergeActivityDocs(projectId, stage);
      saveFile(result);
      setState({ kind: "done", result });
    } catch (err) {
      setState({ kind: "error", message: API.errorMessage(err) });
    }
  };
  return (
    <span className="merge-docs">
      <button type="button" className={`btn btn-secondary${size === "sm" ? " btn-sm" : ""}`} title={mode ? `Berisi aktivitas mode ${mode} saja` : undefined} onClick={run} disabled={state.kind === "busy"} aria-busy={state.kind === "busy"}>
        <Glyph name="documentDownload" size={16} />
        <span>{state.kind === "busy" ? "Menggabungkan..." : label}</span>
      </button>
      {state.kind === "done" ? (
        <span className="merge-docs-note" role="status">
          {mergeNote(state.result, mode)}
        </span>
      ) : state.kind === "error" ? (
        <span className="merge-docs-note" data-tone="danger" role="alert">
          {state.message}
        </span>
      ) : mode && modeHint === "line" ? (
        <span className="merge-docs-note">Berisi aktivitas mode {mode} saja.</span>
      ) : null}
    </span>
  );
}
